import { SubmissionStatus } from '@bp/bp-graphql-types';
import { CalendarIcon, Chip, SendIcon, Tooltip } from '@bp/ui-components';
import { BpCard } from 'components/BpCard/BpCard';
import { StatusChip } from 'components/StatusChip/StatusChip';
import { useSubmissions } from 'components/Submissions/hooks/useSubmissions';
import { DesktopContext } from 'context/IsDesktop';
import { useRequireParams } from 'hooks/useRequireParams';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SubmissionInfoCard.module.scss';

type SubmissionInfoCardProps = {
  submissionUuid: string;
};

export const SubmissionInfoCard = ({ submissionUuid }: SubmissionInfoCardProps) => {
  const { t } = useTranslation();
  const { courseUuid, assignmentUuid } = useRequireParams(['courseUuid', 'assignmentUuid']);
  const { isDesktop } = useContext(DesktopContext);

  const { submissions } = useSubmissions({ courseUuid, assignmentUuid });

  const submission = submissions.find((s) => s.uuid === submissionUuid);
  if (!submission) throw new Error('Submission not found');

  const status = submission.status;

  return (
    <BpCard className={styles['submission-info-card']}>
      <div className={styles.top}>
        {isDesktop && <CalendarIcon className={styles.icon} />}
        {submission.remainingDays < 0 && (
          <div>
            {t('assignments.overdue')}
            {submission.isEditable ? ', ' + t('submissions.delayable') : ''}
          </div>
        )}
        {submission.remainingDays > -1 && (
          <>
            <div className={styles.days}>{submission.remainingDays}</div>
            <div className='ml-3'>{t('common.daysLeft')}</div>
          </>
        )}
      </div>

      <div className={styles.bottom}>
        {isDesktop && <SendIcon className={styles.icon} />}
        <StatusChip status={status} />
        {submission.public && (
          <Tooltip content={t('submissions.publicisedHint')} triggerClass={styles.publicised}>
            <Chip value={t('submissions.publicised')} bgColor='var(--color-error-light)' color='var(--color-white)' />
          </Tooltip>
        )}
      </div>
    </BpCard>
  );
};
